import React, { useMemo, ReactNode } from "react"
import { Col, Form, Row, Select, Button, Descriptions, Empty, Alert, Divider, Flex, Result } from "antd"
import { useForm, useWatch } from "antd/lib/form/Form"

import Utilitario from "@/Utils/Utilitario"
import { useObterTokenDePagamento } from "@/Services/Domain/Viagem/ViagemService"
import { TypeInscricaoDoAlunoNaViagem } from "@/Models/Domain/Viagem/InscricaoDoAlunoNaViagem"
import { TypeViagem } from "@/Models/Domain/Viagem/Viagem"
import { TypeFormaDePagamentoDaViagem } from "@/Models/Domain/Viagem/FormaDePagamentoDaViagem"
import { useInscricaoDescontoBuscarPorViagemAluno } from "@/Services/Domain/Viagem/Inscricao/InscricaoDescontoService"
import { EnumTipoValor } from "@/Models/Domain/Viagem/Inscricao/InscricaoDesconto"

export const PagamentoDaViagem: React.FC<{
    viagem: TypeViagem,
    inscricaoAtual: TypeInscricaoDoAlunoNaViagem | undefined,
    textoCancelar?:ReactNode,
    onCancelar?:() => void
}> = ({viagem, inscricaoAtual, onCancelar, textoCancelar}) => {
    const formasDePagamento = viagem.formasDePagamento;

    interface IFormularioDePagamento {
        formaDePagamento: TypeFormaDePagamentoDaViagem
    }

    const [form] = useForm<IFormularioDePagamento>()
    const valoresDoFormulario = useWatch([], form);

    const {
        data : desconto,
        isLoading : isLoadingDesconto
    } = useInscricaoDescontoBuscarPorViagemAluno({
        codViagem : viagem.codigo!,
        codPessoaAluno : inscricaoAtual?.aluno?.pessoa?.codigo!,
        enabled : !isNaN(viagem.codigo!) && viagem.codigo! > 0 && !isNaN(inscricaoAtual?.aluno?.pessoa?.codigo!) && inscricaoAtual?.aluno?.pessoa?.codigo! > 0
    });

    const formaDePagamentoSelecionada = useMemo(() => {
        const codigo = valoresDoFormulario?.formaDePagamento?.codigo;
        const fp = viagem.formasDePagamento?.find((v) => v.codigo == codigo);
        return fp;
    }, [valoresDoFormulario]);
    
    const valorDesconto = useMemo(() => {
        const valorTotal = (formaDePagamentoSelecionada?.valorTotal ?? 0);
        if (desconto === null || desconto === undefined)
            return 0;
        else if (desconto.codTipoValor === EnumTipoValor.PercValor)
            return valorTotal * (desconto.valor / 100.0)
        else if (desconto.codTipoValor === EnumTipoValor.ValorFixo)
            return desconto.valor < valorTotal ? desconto.valor : valorTotal;
        else return 0;
    }, [formaDePagamentoSelecionada?.valorTotal, desconto]);

    const valorAPargar = useMemo(() => {
        const valorComDesconto = (formaDePagamentoSelecionada?.valorTotal ?? 0) - valorDesconto;
        return valorComDesconto > 0 ? valorComDesconto : 0;
    }, [formaDePagamentoSelecionada?.valorTotal, valorDesconto]);

    const obterURIDePagamentoMutation = useObterTokenDePagamento({});

    const aoFinalizar = (valores: IFormularioDePagamento) => {
        const codigoDaFormaDePagamento = valores.formaDePagamento?.codigo;
        const codigoDaInscricao = inscricaoAtual?.codigo;

        if(codigoDaFormaDePagamento == undefined || codigoDaInscricao == undefined) return;
        const w = window.open();

        obterURIDePagamentoMutation.mutate(
            { 
                codigoDaInscricao,
                codigoDaFormaDePagamento 
            }, 
            {
                onSuccess: (data) => {
                    const uri = data.data.token;

                    if(uri != undefined){
                        const caminhoDePagamento = "/Financeiro/Pagamento?urlRedirectSucess=/Financeiro/Pagamento/Sucesso&";
                        if(w) w.location.href = caminhoDePagamento + uri;
                    }
                },
                onError: () => {
                    if(w) w.close();
                }
            }
        )
    }

    if(inscricaoAtual?.codigo == undefined) return <Empty/>;

    if (inscricaoAtual?.status == 3) return <Result
        status="success"
        title="Inscrito e pago"
        subTitle={'Sua incrição encontra-se "Inscrito e pago", caso ainda não tenha feito o pagamento contate o suporte.'}
        extra={
            <Button type="primary" onClick={onCancelar}>
                Voltar
            </Button>
        }
    />;

    return <React.Fragment>
        <Form form={form} layout="vertical" autoComplete="off" onFinish={aoFinalizar}>
            <Row gutter={[16, 16]}>
                <Col xs={24} md={16} lg={12}>
                    <Form.Item<IFormularioDePagamento>
                        label="Forma de pagamento"
                        name={["formaDePagamento", "codigo"]}
                        rules={[
                            { required: true, message: "Selecione a forma de pagamento!" }
                        ]}
                        style={{marginBottom: 0}}
                    >
                        <Select
                            allowClear
                            placeholder="Selecione a forma de pagamento"
                            options={formasDePagamento?.map((fp) => ({
                                label: `${fp.descricao} - ${fp.valorTotal ? Utilitario.toStringDinheiro(fp.valorTotal) : "N/A"}`,
                                value: fp?.codigo
                            }))}
                            disabled={isLoadingDesconto ? true : undefined}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24}>
                    {formaDePagamentoSelecionada &&
                        <Descriptions title="Forma de Pagamento" layout="horizontal"
                            style={{ marginBottom: "8px" }}
                        >
                            <Descriptions.Item label="Descrição">{formaDePagamentoSelecionada.descricao}</Descriptions.Item>
                            <Descriptions.Item label="Valor viagem">{Utilitario.toStringDinheiro(formaDePagamentoSelecionada?.valorTotal ?? 0)}</Descriptions.Item>
                            {valorDesconto > 0 && <Descriptions.Item label="Valor desconto">{Utilitario.toStringDinheiro(valorDesconto * -1)}</Descriptions.Item>}
                            <Descriptions.Item label="Valor a pagar">{Utilitario.toStringDinheiro(valorAPargar)}</Descriptions.Item>
                            <Descriptions.Item label="Parcelas">{formaDePagamentoSelecionada.parcelas}</Descriptions.Item>
                            {/* <Descriptions.Item label="Meio de Pagamento">{formaDePagamentoSelecionada.meioDePagamento?.nome ?? 'N/A'}</Descriptions.Item> */}
                        </Descriptions>
                    }

                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                {
                    valoresDoFormulario?.formaDePagamento?.codigo != undefined &&
                    <Col xs={24}>
                        <Alert
                            message={
                                <>
                                    Ao clicar em 'Pagar agora' você será redirecionado para a página de pagamento.
                                </>
                            }
                            type="warning"
                            closable={false}
                        />
                    </Col>
                }

                <Col xs={24} lg={4}>
                    <Form.Item >
                        <Button
                            disabled={valoresDoFormulario?.formaDePagamento?.codigo == undefined}
                            style={{ width: "100%" }} type="primary"
                            htmlType="submit"
                        >
                            Pagar agora
                        </Button>
                    </Form.Item>
                </Col>
                {onCancelar && <Col xs={24}>
                        <Divider />
                        <Flex justify="space-between">
                            <Button type="primary" danger htmlType="reset" onClick={onCancelar}>{textoCancelar ?? "Cancelar"}</Button>
                        </Flex>
                    </Col>
                }
            </Row>
        </Form>
    </React.Fragment>;
}
